import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, 
  FileText, 
  Clock, 
  ArrowRight, 
  Activity,
  TestTubes,
  CheckCircle2,
  AlertCircle,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [results, setResults] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [bookingsRes, resultsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/bookings`),
        axios.get(`${API}/results`),
        axios.get(`${API}/notifications`)
      ]);
      setBookings(bookingsRes.data.slice(0, 5));
      setResults(resultsRes.data.slice(0, 5));
      setNotifications(notificationsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'badge-warning',
      confirmed: 'badge-success',
      processing: 'badge-neutral',
      completed: 'badge-success',
      cancelled: 'badge-error'
    };
    return <span className={styles[status] || 'badge-neutral'}>{status}</span>;
  };

  const stats = [
    { 
      label: 'Upcoming Appointments', 
      value: bookings.filter(b => ['pending', 'confirmed'].includes(b.status)).length,
      icon: Calendar,
      color: 'bg-sky-50 text-sky-600'
    },
    { 
      label: 'Lab Results', 
      value: results.length,
      icon: FileText,
      color: 'bg-emerald-50 text-emerald-600'
    },
    { 
      label: 'Tests Completed', 
      value: bookings.filter(b => b.status === 'completed').length,
      icon: CheckCircle2,
      color: 'bg-purple-50 text-purple-600'
    },
    { 
      label: 'Pending Review', 
      value: results.filter(r => !r.verified_by).length,
      icon: Clock,
      color: 'bg-amber-50 text-amber-600'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="patient-dashboard">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">Here's an overview of your health journey</p>
          </div>
          <Button 
            className="bg-sky-600 hover:bg-sky-700"
            onClick={() => navigate('/tests')}
            data-testid="dashboard-book-test-btn"
          >
            <TestTubes className="w-4 h-4 mr-2" />
            Book New Test
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="card-clinical stat-card" data-testid={`stat-card-${index}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-sm text-slate-600 mt-1">{stat.label}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Bookings */}
          <div className="lg:col-span-2 card-clinical">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Recent Bookings</h2>
              <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')} data-testid="view-all-bookings-btn">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No bookings yet</p>
                <Button variant="link" onClick={() => navigate('/tests')} className="mt-2">
                  Book your first test
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.map((booking) => (
                  <div 
                    key={booking.id} 
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/bookings/${booking.id}`)}
                    data-testid={`booking-item-${booking.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border">
                        <TestTubes className="w-5 h-5 text-sky-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {booking.test_ids?.length || 0} Test(s)
                        </p>
                        <p className="text-sm text-slate-500">
                          {booking.appointment_date} at {booking.appointment_time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(booking.status)}
                      <span className="text-sm font-medium text-slate-700">
                        ${booking.total_amount?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="card-clinical">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
              <Badge variant="secondary">{notifications.filter(n => !n.read).length} new</Badge>
            </div>
            {notifications.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No notifications</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id} 
                    className={`p-3 rounded-lg border ${notif.read ? 'bg-white' : 'bg-sky-50 border-sky-100'}`}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        notif.type === 'result' ? 'bg-emerald-100 text-emerald-600' :
                        notif.type === 'payment' ? 'bg-purple-100 text-purple-600' :
                        'bg-sky-100 text-sky-600'
                      }`}>
                        {notif.type === 'result' ? <FileText className="w-4 h-4" /> :
                         notif.type === 'payment' ? <CheckCircle2 className="w-4 h-4" /> :
                         <Calendar className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{notif.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Results */}
        <div className="card-clinical">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-slate-900">Recent Lab Results</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/results')} data-testid="view-all-results-btn">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          {results.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No lab results yet</p>
              <p className="text-sm text-slate-500 mt-1">Results will appear here once your tests are processed</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((result) => (
                <div 
                  key={result.id} 
                  className="p-4 border rounded-lg hover:border-sky-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/results`)}
                  data-testid={`result-card-${result.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    {result.verified_by ? (
                      <span className="badge-success">Verified</span>
                    ) : (
                      <span className="badge-warning">Pending</span>
                    )}
                  </div>
                  <h3 className="font-medium text-slate-900">{result.test_name}</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {new Date(result.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Health Tips */}
        <div className="bg-sky-50 border border-sky-100 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Health Tip of the Day</h3>
              <p className="text-slate-600 mt-1">
                Regular health check-ups can help detect potential health issues before they become serious. 
                Consider booking a comprehensive health package for preventive care.
              </p>
              <Button variant="link" className="mt-2 p-0 h-auto text-sky-600" onClick={() => navigate('/tests')}>
                Explore Health Packages →
              </Button>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PatientDashboard;
